import {
	Component,
	Inject,
	inject,
	Input,
	OnInit,
	OnDestroy,
	Optional,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialog,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { provideDayjsDateAdapter } from '@lmc-app/shared/adapters/dayjs-date-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Subject } from 'rxjs';

import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { buildRapidApplicationFormConfig } from './rapid-application-form.config';

import {
	ValidationErrorDialog,
	ValidationErrorDialogData,
} from './rapid-application-modal-validation-error-dialog';

import { validateFormControls } from '@lmc-app/shared/formly/validations/application.validation';
import { GoogleAutoAddressCompleteUtils } from '@lmc-app/shared/utils/google-auto-address-complete.utils';
import { getFieldLabelWithStep } from './rapid-application-form-helpers';
import { RapidApplicationFormOptions } from './rapid-application-form-options.interface';
import { MatCardHeader, MatCardTitle } from '@angular/material/card';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'app-rapid-application-modal',
	templateUrl: './rapid-application-modal.component.html',
	styleUrls: ['./rapid-application-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		provideDayjsDateAdapter(),
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline', // Sets the border all the way around
				floatLabel: 'always', // Keeps the label in the floating position
			},
		},
	],
	imports: [
		MatCardHeader,
		MatCardTitle,
		CdkScrollable,
		MatDialogContent,
		ReactiveFormsModule,
		FormlyModule,
		MatDialogActions,
		MatButton,
	],
})
export class RapidApplicationModalComponent implements OnInit, OnDestroy {
	@Input() application: any = null;

	private readonly _onDestroy = new Subject<void>();

	private validationErrors: string[] = [];

	// Single Formly-managed form (stepper pattern)
	protected form = new FormGroup({});

	// Formly field config (stepper with all steps)
	protected fields: FormlyFieldConfig[] = [];

	// Single Formly model (PascalCase keys matching Zoho CRM)
	protected model: any = {};
	private readonly dialog = inject(MatDialog);

	constructor(
		public dialogRef: MatDialogRef<RapidApplicationModalComponent>,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any
	) {
		dialogRef.disableClose = true;
	}

	public ngOnInit(): void {
		this.buildFormlyConfigs();
		this.patchModel();
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	populateDemo(index: number) {
		switch (index) {
			case 1:
				this.model = {
					...this.model,
					resident_status: 'Citizen',
					first_name: 'WILLIEMAE',
					middle_name: '',
					last_name: 'GIANDOMENICO',
					sin: '',
					loyalty_program: 'No',
					dob: new Date('1968-08-19'),
					email_address: 'WILLIEMAE@test.com',
					telephone: '4376382702',
					street: '',
					address2: '',
					city: '',
					province: '',
					postal_code: 'P0E1N0',
				};
				break;
			default:
				break;
		}
	}

	protected onDismiss(): void {
		this.dialogRef.close(false);
	}

	protected buildFormlyConfigs() {
		const options: RapidApplicationFormOptions = {
			onAddressSelect: (result: any) => this.handleAddressSelect(result),
		};
		this.fields = buildRapidApplicationFormConfig(options);
	}

	private handleAddressSelect(result: any) {
		const parsedAddress =
			GoogleAutoAddressCompleteUtils.parseAddressComponents(result);

		this.model = {
			...this.model,
			street: parsedAddress.address1,
			city: parsedAddress.city,
			province: parsedAddress.province,
			postal_code: parsedAddress.postalCode,
		};
	}

	private patchModel() {
		this.model = { ...this.data };
	}

	protected async submitApplication() {
		// Validate form before submitting
		if (!this.validateForm()) {
			// Show validation errors in dialog
			const dialogData: ValidationErrorDialogData = {
				errors: this.validationErrors,
			};

			this.dialog.open(ValidationErrorDialog, {
				width: '600px',
				data: dialogData,
			});

			return;
		}

		this.dialogRef.close(this.model);
	}

	validateForm(): boolean {
		this.validationErrors = [];

		// Mark all fields as touched to show validation errors
		this.form.markAllAsTouched();

		// Validate regular form controls using reusable utility
		if (this.form.invalid) {
			validateFormControls(this.form, this.validationErrors, (fields, key) =>
				getFieldLabelWithStep(this.fields, key)
			);

			return false;
		}

		return true;
	}
}
